// apps/web/src/data/vocab/topic-display.ts

import type { TopicKey } from "./types";

/** Curated icon set for topic cards. Each key maps to a stroke-based SVG in
 *  `components/woorden/TopicIcon.tsx`. Static config — not a backend field. */
export type IconKey =
  | "sparkles"
  | "wand"
  | "clock"
  | "users"
  | "heart"
  | "utensils"
  | "shirt"
  | "home"
  | "cart"
  | "briefcase"
  | "book"
  | "ball"
  | "bus"
  | "wallet"
  | "monitor"
  | "leaf"
  | "cloud"
  | "scale"
  | "question"
  | "history"
  | "layout";

export type TopicDisplay = {
  /** Section title shown in eyebrow + h1. Tracks the master-sheet headings. */
  title: string;
  /** One-line italic explainer below the h1 on the topic detail page; reused
   *  as the sub-line on the topic card in the grid. */
  blurb: string;
  /** Curated icon for the topic card tile. */
  icon: IconKey;
};

export const TOPIC_DISPLAY: Readonly<Record<TopicKey, TopicDisplay>> = {
  "super-zinnen": {
    title: "10 super-zinnen",
    blurb: "De zinnen die elk antwoord winnen — drill totdat ze automatisch zijn.",
    icon: "sparkles",
  },
  "magic-words": {
    title: "Magic words",
    blurb: "Vaste uitdrukkingen voor plaats, tijd en routine — uit het hoofd leren.",
    icon: "wand",
  },
  "numbers-time": {
    title: "Cijfers, tijd, datums",
    blurb: "Getallen, tijd zeggen, dagen, maanden en jaartallen.",
    icon: "clock",
  },
  "familie-mensen": {
    title: "Familie & mensen",
    blurb: "Familieleden en mensen om u heen.",
    icon: "users",
  },
  "lichaam-gezondheid": {
    title: "Lichaam & gezondheid",
    blurb: "Lichaamsdelen, klachten en bezoek aan de dokter.",
    icon: "heart",
  },
  "eten-drinken": {
    title: "Eten & drinken",
    blurb: "Eten, drinken en koken — voor Deel 4 voorkeursvragen.",
    icon: "utensils",
  },
  kleding: {
    title: "Kleding",
    blurb: "Kledingstukken voor beschrijvingen en boodschappen.",
    icon: "shirt",
  },
  "huis-kamers": {
    title: "Huis & kamers",
    blurb: "Kamers en meubels — handig voor Deel 2 plaatjes.",
    icon: "home",
  },
  boodschappen: {
    title: "Boodschappen",
    blurb: "Wat u koopt en waar — markt, supermarkt, winkel.",
    icon: "cart",
  },
  "werk-beroepen": {
    title: "Werk & beroepen",
    blurb: "Beroepen en wat u op uw werk doet.",
    icon: "briefcase",
  },
  onderwijs: {
    title: "Onderwijs",
    blurb: "School, leren en de inburgeringscursus.",
    icon: "book",
  },
  "vrije-tijd-sport": {
    title: "Vrije tijd & sport",
    blurb: "Wat u in uw vrije tijd doet en welke sport u beoefent.",
    icon: "ball",
  },
  vervoer: {
    title: "Vervoer",
    blurb: "Hoe u zich verplaatst — en met welk voorzetsel.",
    icon: "bus",
  },
  "geld-bank": {
    title: "Geld & bank",
    blurb: "Betalen, sparen en bankzaken.",
    icon: "wallet",
  },
  digitaal: {
    title: "Digitaal",
    blurb: "Computer, internet en DigiD — nieuw in 2025/2026 KNM.",
    icon: "monitor",
  },
  duurzaamheid: {
    title: "Duurzaamheid",
    blurb: "Milieu, recyclen en energie — nieuw in 2025/2026 KNM.",
    icon: "leaf",
  },
  "weer-natuur": {
    title: "Weer & natuur",
    blurb: "Het weer en de seizoenen.",
    icon: "cloud",
  },
  "adjectives-comparatives": {
    title: "Adjectieven & vergelijkingen",
    blurb: "Bijvoeglijke naamwoorden en hun vergrotende/overtreffende trap.",
    icon: "scale",
  },
  vraagwoorden: {
    title: "Vraagwoorden",
    blurb: "Wie, wat, waar, wanneer, waarom — de basis voor Deel 1.",
    icon: "question",
  },
  "irregular-past-tense": {
    title: "Onregelmatige verleden tijd",
    blurb: "De onregelmatige werkwoorden die u in Deel 4 nodig heeft.",
    icon: "history",
  },
  "templates-by-deel": {
    title: "Zinpatronen per deel",
    blurb: "Standaardzinnen per examendeel — proza, geen losse woorden.",
    icon: "layout",
  },
} as const;
